import { useFlag } from '@innovamat/flags';
import { type TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useOrganization } from '../../classrooms-management';
import { type Organization } from '../../classrooms-management/modules/permission/organization';
import { useUser } from '../../user-management';
import type {
  ProcessedSectionItem,
  ProcessedSitemapSection,
  ProcessedSitemapTree,
} from '../types';
import { APP_PATHS } from '../utils';

type Props = {
  showAdminDashboard: boolean;
};

const classrooms = (t: TFunction): ProcessedSitemapSection => ({
  menuItemType: 'item',
  id: `classrooms`,
  value: t('sidebar.items.classrooms-list'),
  icon: 'ClassIcon',
  route: APP_PATHS.CLASSROOMS_URL,
});

const mathJourneyGroups = (
  enabled: boolean,
  t: TFunction
): ProcessedSitemapSection | null => {
  if (!enabled) {
    return null;
  }

  return {
    menuItemType: 'item',
    id: `journeys`,
    value: t('sidebar.items.journeys-list'),
    icon: 'JourneysIcon',
    route: APP_PATHS.JOURNEYS_URL,
    dataAttributes: {
      'data-inlinemanual': 'journeys-menu-item',
    },
  };
};

const schoolPeople = (
  organization: Organization,
  t: TFunction
): ProcessedSitemapSection => ({
  menuItemType: 'section',
  id: `school-people`,
  value: t('sidebar.items.people'),
  icon: 'StudentsIcon',
  items: [
    {
      id: `school-students`,
      value: t('sidebar.items.students'),
      subtle: organization ? `(${organization?.numberOfStudents})` : '',
      route: APP_PATHS.SCHOOL_STUDENTS_URL,
    },
    {
      id: `deleted-students`,
      value: t('sidebar.items.deletedStudents'),

      subtle: organization ? `(${organization?.numberOfDeletedStudents})` : '',
      route: APP_PATHS.DELETED_STUDENTS_URL,
    },
    {
      id: `school-teachers`,
      value: t('sidebar.items.teachers'),
      subtle: organization ? `(${organization?.numberOfTeachers})` : '',
      route: APP_PATHS.TEACHERS_URL,
    },
  ],
});

const currentMaterials = (
  enabled: boolean,
  t: TFunction
): ProcessedSectionItem | null => {
  if (!enabled) {
    return null;
  }

  return {
    id: 'current-materials',
    value: t('sidebar.items.currentMaterial'),
    route: '/orders-returns/current-materials',
  };
};

const familyPayments = (
  enabled: boolean,
  t: TFunction
): ProcessedSectionItem | null => {
  if (!enabled) {
    return null;
  }

  return {
    id: 'family-payments',
    value: t('sidebar.items.familyPayments'),
    route: '/orders-returns/family-payments',
  };
};

const ordersReturnsHistory = (t: TFunction): ProcessedSectionItem => ({
  id: 'history',
  value: t('sidebar.items.ordersReturnsHistory'),
  route: '/orders-returns/history',
});

const ordersReturns = (
  currentMaterialsEnabled: boolean,
  familyPaymentsEnabled: boolean,
  t: TFunction
): ProcessedSitemapSection => ({
  menuItemType: 'section',
  id: 'orders-returns',
  value: t('sidebar.items.ordersAndReturns'),
  icon: 'OrdersIcon',
  items: [
    currentMaterials(currentMaterialsEnabled, t),
    familyPayments(familyPaymentsEnabled, t),
    ordersReturnsHistory(t),
  ].filter((item): item is ProcessedSectionItem => item !== null),
});

const imports = (enabled: boolean, t: TFunction): ProcessedSitemapSection => {
  if (!enabled) {
    return null;
  }

  return {
    menuItemType: 'item',
    id: `imports`,
    value: t('sidebar.items.imports-section'),
    route: '/imports',
    icon: 'ImportIcon',
  };
};

const adminDashboard = (
  enabled: boolean,
  t: TFunction
): ProcessedSitemapSection => {
  if (!enabled) {
    return null;
  }

  return {
    menuItemType: 'item',
    id: `admin-dashboard`,
    value: t('sidebar.items.adminDashboard'),
    route: '/admin-dashboard',
    icon: 'CbStatisticProbabilityIcon',
  };
};

export const useGetAdminSitemap = ({
  showAdminDashboard,
}: Props): ProcessedSitemapTree => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { organization, isRosteringEnabled, isJourneysEnabled } =
    useOrganization();

  // TODO cambiar estas opciones por capabilities
  const showJourneysList = isJourneysEnabled;
  const showImportsList = !isRosteringEnabled;

  const featureFlagUser = {
    identifier: '',
    email: user?.email ?? '',
    custom: {
      schoolId: user?.organizationId ?? '',
      region: organization?.region ?? '',
    },
  };

  const { value: showCurrentMaterials } = useFlag(
    'shouldShowCurrentMaterials',
    false,
    featureFlagUser
  );

  const { value: showFamilyPayments } = useFlag(
    'showfamilypayments',
    false,
    featureFlagUser
  );

  const sitemap = useMemo(() => {
    return [
      classrooms(t),
      mathJourneyGroups(showJourneysList, t),
      schoolPeople(organization as Organization, t),
      adminDashboard(showAdminDashboard, t),
      imports(showImportsList, t),
      ordersReturns(showCurrentMaterials, showFamilyPayments, t),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    organization,
    showAdminDashboard,
    showCurrentMaterials,
    showFamilyPayments,
    showImportsList,
    showJourneysList,
  ]);

  return sitemap;
};
